import * as React from 'react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, CollapsableCard, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  AttendanceEnumsBalanceCarryoverResetFrequencyEnum,
  SettingPageAttendanceBalanceCarryoverFragment,
  SettingPageEmployeesSettingFragment,
  TimeTrackingClockInPageQuery
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import ConfirmUpdateModal from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/shared/ConfirmUpdateModal'
import {
  getResetFrequencyOptions,
  getResetFrequencyPeriod,
  getTransferFrequencyOptions,
  isTransferFrequencyCompatibleWithPools,
  validateTransferAndResetCompatibility
} from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/hoursCalculationUtils'
import { useCreateAttendanceBalanceCarryover } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useCreateAttendanceBalanceCarryover'
import { useUpdateAttendanceBalanceCarryover } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceBalanceCarryover'
import useUpdateAttendanceSetting from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceSetting'
import AttendanceSettingForm from 'modules/attendance/lib/forms/AttendanceSettingForm'
import BalanceCarryoverForm from 'modules/attendance/lib/forms/BalanceCarryoverForm'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

type AttendanceOvertimePool =
  TimeTrackingClockInPageQuery['attendance']['overtimePoolsConnection']['nodes'][number]

interface Props {
  attendanceSetting: AttendanceSetting
  balanceCarryover?: SettingPageAttendanceBalanceCarryoverFragment
  overtimePools: AttendanceOvertimePool[]
}

const BankOfHoursCounter = ({ attendanceSetting, balanceCarryover, overtimePools }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { createBalanceCarryover } = useCreateAttendanceBalanceCarryover()
  const { updateBalanceCarryover } = useUpdateAttendanceBalanceCarryover()

  const createBalanceCarryoverForm = (
    attendanceBalanceCarryover?: SettingPageAttendanceBalanceCarryoverFragment
  ) =>
    new BalanceCarryoverForm({
      frequency_number: attendanceBalanceCarryover?.frequencyNumber || 1,
      reset_frequency: attendanceBalanceCarryover?.resetFrequency || 'monthly'
    })

  const [balanceCarryoverForm, _setBalanceCarryoverForm] = React.useState(
    createBalanceCarryoverForm(balanceCarryover)
  )

  const createSettingForm = (setting: AttendanceSetting) =>
    new AttendanceSettingForm({
      extra_hours_transfer_frequency: setting.attributes.extra_hours_transfer_frequency
    })

  const [settingForm, setSettingForm] = React.useState(createSettingForm(attendanceSetting))
  const extraHoursTransferFrequencyField = settingForm.get('extra_hours_transfer_frequency')
  const balanceResetFrequencyField = balanceCarryoverForm.get('reset_frequency')
  const balanceFrequencyNumberField = balanceCarryoverForm.get('frequency_number')

  const { loading, updateSetting } = useUpdateAttendanceSetting(settingForm, attendanceSetting)
  const [selectedTransferFrequency, setSelectedTransferFrequency] = React.useState(
    attendanceSetting.extraHoursTransferFrequency
  )
  const [selectedResetFrequency, setSelectedResetFrequency] = React.useState(
    balanceCarryover?.resetFrequency || 'monthly'
  )
  const [validationError, setValidationError] = React.useState<string | undefined>()
  const [showUpdateConfirmation, setShowUpdateConfirmation] = React.useState(false)
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)

  const validateTransferAndReset = () =>
    validateTransferAndResetCompatibility(
      extraHoursTransferFrequencyField.value,
      balanceResetFrequencyField.value
    )

  useUnsafeEffect(() => {
    setSettingForm(createSettingForm(attendanceSetting))
    setSelectedTransferFrequency(attendanceSetting.extraHoursTransferFrequency)
    setSelectedResetFrequency(balanceCarryover?.resetFrequency || 'monthly')
  }, [attendanceSetting, balanceCarryover?.resetFrequency])

  const saveBalanceCarryover = async (attendanceSettingId: number) => {
    const frequencyValue = balanceFrequencyNumberField.value
    const frequencyNumber = parseInt(frequencyValue) || 1
    const resetFrequency = balanceResetFrequencyField.value

    if (balanceCarryover?.id) {
      await updateBalanceCarryover({
        id: balanceCarryover.id,
        attendanceEmployeesSettingId: attendanceSettingId,
        resetFrequency,
        frequencyNumber
      })
    } else {
      await createBalanceCarryover({
        attendanceEmployeesSettingId: attendanceSettingId,
        resetFrequency,
        frequencyNumber
      })
    }

    balanceCarryoverForm.cleanAll()
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
  }

  const onSubmit = () => {
    const validation = validateTransferAndReset()
    if (!validation.isValid) {
      return
    }

    updateSetting()
    if (balanceCarryoverForm.isDirty) {
      saveBalanceCarryover(attendanceSetting.attributes.id)
    }
    onClose()
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const validation = validateTransferAndReset()
    if (!validation.isValid) {
      return
    }

    const settingId = +newSetting.id

    if (balanceCarryoverForm.isDirty) {
      await saveBalanceCarryover(settingId)
    }

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'work_time_categories'
    }

    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onChangeFrequency = (option: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum) => {
    extraHoursTransferFrequencyField.set(option)
    setSelectedTransferFrequency(option)
    const validation = validateTransferAndReset()
    setValidationError(validation.errorMessage)
  }

  const onChangeResetFrequency = (option: AttendanceEnumsBalanceCarryoverResetFrequencyEnum) => {
    balanceResetFrequencyField.set(option)
    setSelectedResetFrequency(option)
    const validation = validateTransferAndReset()
    setValidationError(validation.errorMessage)
  }

  const handleConfirmUpdate = () => {
    setShowUpdateConfirmation(false)
    setOpenEffectiveAtFormModal(true)
  }

  const handleCancelUpdate = () => {
    setShowUpdateConfirmation(false)
  }

  const handleSave = () => {
    const needsConfirmation = !isTransferFrequencyCompatibleWithPools(
      extraHoursTransferFrequencyField.value,
      overtimePools
    )

    if (needsConfirmation) {
      setShowUpdateConfirmation(true)
    } else {
      setOpenEffectiveAtFormModal(true)
    }
  }

  const transferFrequencyOptions = getTransferFrequencyOptions()
  const resetFrequencyOptions = getResetFrequencyOptions()
  const showPeriodForm = selectedResetFrequency !== 'never'
  const disableSubmitButton = !!validationError || loading

  return (
    <>
      <CollapsableCard
        open
        Card={
          <Box
            flexDirection="row"
            gap="s8"
            alignItems="center"
            paddingLeft="s12"
            paddingRight="s16"
          >
            <Box flexGrow>
              <Icon.Medium icon="balance" color="grey800" />
              <Box paddingBottom="s12" paddingTop="s16">
                <Text size="300" weight="bold">
                  {i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_title'
                  )}
                </Text>
              </Box>
              <Text size="300">
                {i18n.t(
                  'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_description'
                )}
              </Text>
            </Box>
          </Box>
        }
        Content={
          <Box gap="s16" paddingLeft="s4">
            <Box gap="s4">
              <Text size="200" weight="semibold">
                {i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.title'
                )}
              </Text>
              <Text size="200">
                {i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.description'
                )}
              </Text>
            </Box>

            <Select
              field={extraHoursTransferFrequencyField}
              value={selectedTransferFrequency}
              options={transferFrequencyOptions}
              onChange={(field) => onChangeFrequency(field.value)}
              data-testid="boh-calculation-frequency-select"
            />

            <Box gap="s8" data-testid="boh-reset-section">
              <Box gap="s4">
                <Text size="200" weight="semibold">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.title'
                  )}
                </Text>
                <Text size="200">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.description'
                  )}
                </Text>
              </Box>
              <Box data-testid="boh-reset-frequency-select">
                <Select
                  field={balanceResetFrequencyField}
                  value={selectedResetFrequency}
                  options={resetFrequencyOptions}
                  onChange={(field) => onChangeResetFrequency(field.value)}
                />
              </Box>

              {showPeriodForm && (
                <>
                  <Box flexDirection="row" gap="s8" alignItems="center">
                    <Box flexGrow gap="s4" data-testid="boh-interval">
                      <Text.Normal size="200" color="grey1000" weight="medium">
                        {i18n.t(
                          'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.interval'
                        )}
                      </Text.Normal>
                      <TextInput
                        field={balanceFrequencyNumberField}
                        mandatory
                        readonly={balanceCarryoverForm.disableFrequencyNumber}
                        data-testid="boh-interval-input"
                      />
                    </Box>
                    <Box flexGrow gap="s4" data-testid="boh-period">
                      <Text.Normal size="200" color="grey1000" weight="medium">
                        {i18n.t(
                          'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.period'
                        )}
                      </Text.Normal>
                      <TextField
                        readonly
                        value={getResetFrequencyPeriod(balanceResetFrequencyField.value)}
                        data-testid="boh-period-field"
                      />
                    </Box>
                  </Box>
                  <Box flexDirection="row" gap="s4">
                    <Text size="200" weight="bold">
                      {i18n.t(
                        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.note'
                      )}
                    </Text>
                    <Text size="200">
                      {i18n.t(
                        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.foot_note'
                      )}
                    </Text>
                  </Box>
                </>
              )}
            </Box>

            {validationError && (
              <Box padding="s8">
                <Callout type="warning" title="" description={validationError} />
              </Box>
            )}

            {(settingForm.isDirty || balanceCarryoverForm.isDirty) && (
              <Box alignItems="flexEnd">
                <Button type="primary" disabled={disableSubmitButton} onClick={handleSave}>
                  {i18n.t('common.save')}
                </Button>
              </Box>
            )}
          </Box>
        }
      />
      {showUpdateConfirmation && (
        <ConfirmUpdateModal onClose={handleCancelUpdate} onConfirm={handleConfirmUpdate} />
      )}
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onSubmit}
          onClose={onClose}
          running={loading}
          attendanceSettingId={attendanceSetting.attributes.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
      {/* </Section> */}
    </>
  )
}

export default observer(BankOfHoursCounter)
